import React, {useContext, useEffect, useRef, useState} from 'react';
import {useUserConsent} from '@tempo/cookie';

// Module
import LogCenter, {
	MemoryStorage,
	CDP,
	CDPSender,
	DirectStrategy,
} from '@deezer/logcenter';
import config from 'src/config/config.json';

// Hooks
import {usePersonalInfoQuery} from '@graphql/generated';
import {AUTH_STATUS, useAuth} from './AuthProvider';

type LogEvent = {
	(
		event: 'my_deezer_year_used',
		params: {
			cardName: string;
			action: 'interact' | 'share' | 'close';
		},
	): void;
	(
		event: 'screen_view',
		params: {
			cardName: string;
		},
	): void;
};

export type LogProviderState = {
	logEvent: LogEvent;
} | null;

export const LogContext = React.createContext<LogProviderState>(null);

type LogProviderProps = {
	children?: React.ReactNode;
};

const LogProvider: React.FC<LogProviderProps> = ({children}) => {
	const value = useLogProviderContext();

	return <LogContext.Provider value={value}>{children}</LogContext.Provider>;
};

export default LogProvider;

export const useLogContext = (): LogProviderState => {
	const logContext = useContext(LogContext);

	if (!logContext) {
		throw new Error('useLogContext must be used whithin a LogProvider');
	}

	return logContext;
};

/**
 * LogCenter config
 * Start
 */
const memoryStorage = new MemoryStorage();

/* ### SEGMENT ### */
let cdp: CDP;
const getCDP = (): CDP => {
	if (!cdp) {
		cdp = new CDP(config.segment_api_key, {
			logger: (type, error) =>
				// eslint-disable-next-line no-console
				typeof error === 'object' ? console.error({...error, type}) : undefined,
		});
	}
	return cdp;
};

const cdpSender = new CDPSender(getCDP());

const directStrategy = new DirectStrategy(cdpSender, memoryStorage, {
	handledLogTypes: ['cdp'],
});

const logCenterInstance = new LogCenter([directStrategy], memoryStorage);

/**
 * LogCenter config
 * End
 */

const useLogProviderContext = (): LogProviderState | null => {
	const [{authStatus}] = useAuth();

	const {data: personalInfoData} = usePersonalInfoQuery({
		skip:
			authStatus === AUTH_STATUS.LOADING ||
			authStatus === AUTH_STATUS.LOGGED_AS_ANONYMOUS,
	});

	const userId = personalInfoData?.me?.id || null;
	const cdpInstance = useRef(getCDP());
	const [cdpInit, setCdpInit] = useState(false);
	const {consentSettings} = useUserConsent();

	useEffect(() => {
		if (cdpInstance.current && !cdpInit) {
			cdpInstance.current.init();
			setCdpInit(true);
		}
	}, [cdpInit]);

	useEffect(() => {
		if (cdpInit) {
			if (userId && userId !== '0') {
				cdpInstance.current.identify({userId});
			}
		}
	}, [authStatus, cdpInit, userId]);

	useEffect(() => {
		if (cdpInit && consentSettings) {
			cdpInstance.current.setConsent(consentSettings);
		}
	}, [consentSettings, cdpInit]);

	const logEvent: LogEvent = (event, params) => {
		const type = 'cdp';

		switch (event) {
			case 'my_deezer_year_used': {
				if ('action' in params) {
					const {cardName, action} = params;
					logCenterInstance.log({
						type,
						eventName: 'my_deezer_year_used',
						customAttributes: {
							screen_view_name: 'my_deezer_year_card',
							screen_view_id_type: 'card_id',
							screen_view_id: cardName,
							action,
						},
					});
				}

				break;
			}
			case 'screen_view': {
				const {cardName} = params!;
				logCenterInstance.log({
					type,
					eventName: 'screen_view',
					customAttributes: {
						screen_view_name: 'my_deezer_year_card',
						screen_view_id_type: 'card_id',
						screen_view_id: cardName,
					},
				});

				break;
			}
		}
	};

	return {
		logEvent, // Segment
	};
};
